/**
 * Componente de tarjeta de estado de courier
 * Courier status card component
 */

import {
    faBox,
    faClock,
    faExclamationCircle,
    faHourglassHalf,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { CourierStatus } from '../../models/TrackingApiResponse';

interface CourierStatusCardProps {
    courier: CourierStatus;
    onCourierClick?: (courierId: number) => void;
    isSelected?: boolean;
}

// Helper function para formatear el tiempo transcurrido
const formatTimeAgo = (minutes: number | null | undefined): string => {
    if (minutes === null || minutes === undefined || isNaN(minutes)) {
        return 'Nunca';
    }

    if (minutes < 60) {
        return `hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
    } else if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        return `hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
    } else {
        const days = Math.floor(minutes / 1440);
        return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
    }
};

export const CourierStatusCard = ({ courier, onCourierClick, isSelected = false }: CourierStatusCardProps) => {
    const { t } = useTranslation('trackingModule');

    const getStatusBadge = () => {
        switch (courier.status) {
            case 'ok':
                return 'bg-green-500/20 text-green-600 dark:text-green-400';
            case 'warning':
                return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400';
            case 'error':
                return 'bg-red-500/20 text-red-600 dark:text-red-400';
            default:
                return 'bg-card-bg text-subtext';
        }
    };

    const handleClick = () => {
        if (onCourierClick) {
            onCourierClick(courier.courier_id);
        }
    };

    return (
        <div
            data-testid={`courier-status-${courier.courier_id}`}
            onClick={handleClick}
            className={`relative bg-card-bg rounded-lg p-4 border transition-all ${
                isSelected
                    ? 'border-primary cursor-pointer'
                    : 'border-card-border hover:border-primary/50 cursor-pointer'
            }`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-title">
                    {courier.courier_name}
                </h3>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusBadge()}`}>
                    {t(`api_calls.courier_status.status.${courier.status}`)}
                </span>
            </div>

            {/* Metrics */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                            icon={faClock}
                            className="w-3 h-3 text-subtext"
                        />
                        <span className="text-xs text-complementary-text">
                            {t('api_calls.courier_status.last_successful_call')}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-title">
                        {formatTimeAgo(courier.last_successful_call_minutes_ago)}
                    </span>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                            icon={faHourglassHalf}
                            className="w-3 h-3 text-subtext"
                        />
                        <span className="text-xs text-complementary-text">
                            {t('api_calls.courier_status.avg_outdated_minutes')}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-title">
                        {courier.avg_outdated_minutes !== null && courier.avg_outdated_minutes !== undefined
                            ? formatTimeAgo(Math.round(courier.avg_outdated_minutes))
                            : '-'}
                    </span>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                            icon={faBox}
                            className="w-3 h-3 text-subtext"
                        />
                        <span className="text-xs text-complementary-text">
                            {t('api_calls.courier_status.outdated_orders')}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-title">
                        {courier.outdated_orders_count}
                    </span>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="w-3 h-3 text-subtext"
                        />
                        <span className="text-xs text-complementary-text">
                            {t('api_calls.courier_status.never_updated_orders')}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-title">
                        {courier.never_updated_orders_count}
                    </span>
                </div>
            </div>
        </div>
    );
};

